import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import {
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import useCareAdvisorUsers from '../../../../hooks/useCareAdvisorUsers';
import useDebounce from '../../../../hooks/useDebounce';
import { usePersistedRowsPerPage } from '../../../../hooks/usePersistedRowsPerPage';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { IListUsersParams } from '../../../../services/identity/listAllUsers';
import { ClientTag, UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../../../Utils/displayError';
import CADataGrid, { caGridStyles, ColumnDef } from '../Components/CADataGrid';
import styles from './usersPage.module.scss';

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Invited', label: 'Invited' },
  { value: 'Not Invited', label: 'Not Invited' },
  { value: 'Deactivated', label: 'Deactivated' },
];

const TYPE_OPTIONS = [
  { value: UserRoleName.Patient, label: 'Patient' },
  { value: UserRoleName.Dependent, label: 'Dependent' },
];

type ClientTagOption = {
  value: string;
  label: string;
};

const SORT_FIELDS: Record<string, string> = {
  full_name: 'full_name',
  email: 'email',
  client_tags: 'client_tags',
  status: 'status',
  type: 'type',
  external_id: 'external_id',
  id: 'id',
};

const STATUS_PILL_STYLES: Record<string, { color: string; backgroundColor: string }> = {
  Active: { color: '#166534', backgroundColor: '#DCFCE7' },
  Invited: { color: '#C2410C', backgroundColor: '#FFEDD5' },
  'Not Invited': { color: '#B91C1C', backgroundColor: '#FEE2E2' },
  Pending: { color: '#92400E', backgroundColor: '#FEF3C7' },
  Deactivated: { color: '#6B7280', backgroundColor: '#F3F4F6' },
};

const getStatusStyle = (status: string): { color: string; backgroundColor: string } =>
  STATUS_PILL_STYLES[status] ?? { color: '#374151', backgroundColor: '#F3F4F6' };

const SESSION_STATE_KEY = 'dentistry-one:care-advisor:users';

const UsersPage: React.FC = () => {
  const navigate = useRoleNavigate();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);

  const [page, setPage] = useState(() => getSessionNumber(savedState, 'page', 0));
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.CaUsers, 10);
  const rowsPerPageHydratedRef = useRef(false);
  const [sortBy, setSortBy] = useState(() => getSessionString(savedState, 'sort_by', 'full_name'));
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() =>
    getSessionString(savedState, 'sort_direction') === 'desc' ? 'desc' : 'asc',
  );

  const [filterName, setFilterName] = useState(() => getSessionString(savedState, 'full_name'));
  const [filterEmail, setFilterEmail] = useState(() => getSessionString(savedState, 'email'));
  const [filterExternalId, setFilterExternalId] = useState(() => getSessionString(savedState, 'external_id'));
  const [filterInternalId, setFilterInternalId] = useState(() => getSessionString(savedState, 'id'));
  const [filterClientTag, setFilterClientTag] = useState(() => getSessionString(savedState, 'client_tag'));
  const [filterClientDivision, setFilterClientDivision] = useState(() =>
    getSessionString(savedState, 'client_division'),
  );
  const [filterStatus, setFilterStatus] = useState(() => getSessionString(savedState, 'status'));
  const [filterType, setFilterType] = useState(() => getSessionString(savedState, 'type'));
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);

  const debouncedName = useDebounce(filterName, 500);
  const debouncedEmail = useDebounce(filterEmail, 500);
  const debouncedExternalId = useDebounce(filterExternalId, 500);
  const debouncedInternalId = useDebounce(filterInternalId, 500);

  const updateListState = useCallback(
    (
      overrides: Partial<{
        page: number;
        rowsPerPage: number;
        sortBy: string;
        sortDirection: 'asc' | 'desc';
        filterName: string;
        filterEmail: string;
        filterExternalId: string;
        filterInternalId: string;
        filterClientTag: string;
        filterClientDivision: string;
        filterStatus: string;
        filterType: string;
      }> = {},
    ) => {
      writeSessionTableState(SESSION_STATE_KEY, {
        page: overrides.page ?? page,
        rowsPerPage: overrides.rowsPerPage ?? rowsPerPage,
        sort_by: overrides.sortBy ?? sortBy,
        sort_direction: overrides.sortDirection ?? sortDirection,
        full_name: overrides.filterName ?? filterName,
        email: overrides.filterEmail ?? filterEmail,
        external_id: overrides.filterExternalId ?? filterExternalId,
        id: overrides.filterInternalId ?? filterInternalId,
        client_tag: overrides.filterClientTag ?? filterClientTag,
        client_division: overrides.filterClientDivision ?? filterClientDivision,
        status: overrides.filterStatus ?? filterStatus,
        type: overrides.filterType ?? filterType,
      });
    },
    [
      filterClientTag,
      filterClientDivision,
      filterEmail,
      filterExternalId,
      filterInternalId,
      filterName,
      filterStatus,
      filterType,
      page,
      rowsPerPage,
      sortBy,
      sortDirection,
    ],
  );

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const nextRowsPerPage = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    if (nextRowsPerPage !== rowsPerPage) setRowsPerPage(nextRowsPerPage);
  }, [rowsPerPage, savedState, setRowsPerPage]);

  useEffect(() => {
    const fetchClientTags = async (): Promise<void> => {
      try {
        const { nodes } = await listAllClientTags('CareAdvisor');
        const withCode = nodes.filter((t) => Boolean(t.code));
        setClientTagOptions(withCode.map((t) => ({ value: t.code as string, label: t.name })));
        setAllClientTags(withCode);
      } catch (error) {
        displayErrorDetails(error);
      }
    };
    fetchClientTags();
  }, []);

  const clientTagLookup = useMemo(
    () =>
      clientTagOptions.reduce<Record<string, string>>((acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
      }, {}),
    [clientTagOptions],
  );

  const selectedClientTagIdsForDivision = useMemo(
    () => allClientTags.filter((t) => t.code === filterClientTag).map((t) => t.id),
    [allClientTags, filterClientTag],
  );

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const params = useMemo<IListUsersParams>(() => {
    const base: IListUsersParams = {
      sort_by: (SORT_FIELDS[sortBy] || 'full_name') as IListUsersParams['sort_by'],
      sort_direction: sortDirection,
      type: filterType ? [filterType as UserRoleName] : [UserRoleName.Patient, UserRoleName.Dependent],
      limit: rowsPerPage,
      skip: page * rowsPerPage,
      page: page + 1,
    };
    if (debouncedName) base.full_name = debouncedName;
    if (debouncedEmail) base.email = debouncedEmail;
    if (debouncedExternalId) base.external_id = debouncedExternalId;
    if (debouncedInternalId) base.id = debouncedInternalId;
    if (filterClientTag) base.client_tags = [filterClientTag];
    if (filterClientDivision) base.client_division_ids = [Number(filterClientDivision)];
    if (filterStatus) base.status = [filterStatus];
    return base;
  }, [
    page,
    rowsPerPage,
    debouncedName,
    debouncedEmail,
    debouncedExternalId,
    debouncedInternalId,
    filterClientTag,
    filterClientDivision,
    filterStatus,
    filterType,
    sortBy,
    sortDirection,
  ]);

  const { users, totalRecords, dataLoading } = useCareAdvisorUsers(params);

  const getClientTagNames = useCallback(
    (user: UserProfile): string => {
      if (!user.clients || user.clients.length === 0) return '';
      return user.clients
        .map((c) => clientTagLookup[c.name] || c.name)
        .filter(Boolean)
        .join(', ');
    },
    [clientTagLookup],
  );

  const handleSort = useCallback(
    (key: string) => {
      const nextSortDirection = sortBy === key ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      if (sortBy === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortDirection('asc');
      }
      setPage(0);
      updateListState({ page: 0, sortBy: key, sortDirection: nextSortDirection });
    },
    [sortBy, sortDirection, updateListState],
  );

  const handleRowClick = useCallback(
    (user: UserProfile) => {
      navigate(`/users/${user.id}`);
    },
    [navigate],
  );

  const handleAddNewUser = useCallback(() => {
    navigate('/users/new');
  }, [navigate]);

  const columns: ColumnDef<UserProfile>[] = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        widthPx: 180,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.full_name}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder="Name"
            value={filterName}
            onChange={(e) => {
              setFilterName(e.target.value);
              setPage(0);
              updateListState({ filterName: e.target.value, page: 0 });
            }}
            fullWidth
          />
        ),
      },
      {
        key: 'email',
        label: 'Email address',
        widthPx: 240,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.email}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder="Email"
            value={filterEmail}
            onChange={(e) => {
              setFilterEmail(e.target.value);
              setPage(0);
              updateListState({ filterEmail: e.target.value, page: 0 });
            }}
            fullWidth
          />
        ),
      },
      {
        key: 'external_id',
        label: 'External ID',
        widthPx: 160,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.external_id || '-'}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder="External ID"
            value={filterExternalId}
            onChange={(e) => {
              setFilterExternalId(e.target.value);
              setPage(0);
              updateListState({ filterExternalId: e.target.value, page: 0 });
            }}
            fullWidth
          />
        ),
      },
      {
        key: 'id',
        label: 'Internal ID',
        widthPx: 140,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.id}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder="Internal ID"
            value={filterInternalId}
            onChange={(e) => {
              setFilterInternalId(e.target.value);
              setPage(0);
              updateListState({ filterInternalId: e.target.value, page: 0 });
            }}
            fullWidth
          />
        ),
      },
      {
        key: 'client_tags',
        label: 'Client Tag(s)',
        widthPx: 180,
        renderCell: (row) => <span className={caGridStyles.cellText}>{getClientTagNames(row)}</span>,
        filterContent: (
          <Select
            size="small"
            value={filterClientTag}
            onChange={(e: SelectChangeEvent) => {
              setFilterClientTag(e.target.value);
              setFilterClientDivision('');
              setPage(0);
              updateListState({ filterClientTag: e.target.value, filterClientDivision: '', page: 0 });
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">Client Tag</MenuItem>
            {clientTagOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'client_divisions',
        label: 'Division(s)',
        widthPx: 160,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>{(row.divisions || []).map((d) => d.name).join(', ')}</span>
        ),
        filterContent: (
          <Select
            size="small"
            value={filterClientDivision}
            disabled={!filterClientTag}
            onChange={(e: SelectChangeEvent) => {
              setFilterClientDivision(e.target.value);
              setPage(0);
              updateListState({ filterClientDivision: e.target.value, page: 0 });
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">{filterClientTag ? 'Division' : 'Select a client tag first'}</MenuItem>
            {divisionFilterOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        widthPx: 140,
        renderCell: (row) => {
          const { color, backgroundColor } = getStatusStyle(row.status);
          return (
            <span className={caGridStyles.statusChip} style={{ color, backgroundColor }}>
              {row.status}
            </span>
          );
        },
        filterContent: (
          <Select
            size="small"
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => {
              setFilterStatus(e.target.value);
              setPage(0);
              updateListState({ filterStatus: e.target.value, page: 0 });
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">Status</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        widthPx: 140,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.role.name}</span>,
        filterContent: (
          <Select
            size="small"
            value={filterType}
            onChange={(e: SelectChangeEvent) => {
              setFilterType(e.target.value);
              setPage(0);
              updateListState({ filterType: e.target.value, page: 0 });
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">Patient, Dependent</MenuItem>
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
    ],
    [
      filterName,
      filterEmail,
      filterExternalId,
      filterInternalId,
      filterClientTag,
      filterClientDivision,
      divisionFilterOptions,
      filterStatus,
      filterType,
      clientTagOptions,
      getClientTagNames,
      updateListState,
    ],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Users</h1>
        <div className={styles.headerActions}>
          <button type="button" className={styles.createBtn} onClick={handleAddNewUser}>
            <FiPlus />
            <span>Add new user</span>
          </button>
        </div>
      </div>

      <CADataGrid
        columns={columns}
        rows={users}
        getRowId={(row) => row.id}
        onRowClick={handleRowClick}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(p) => {
          setPage(p);
          updateListState({ page: p });
        }}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
          updateListState({ rowsPerPage: rpp, page: 0 });
        }}
        loading={dataLoading}
        columnVisibilityKey={ColumnVisibilityKey.CaUsers}
      />
    </div>
  );
};

export default UsersPage;
